import { useState } from "react";
import { useTranslation } from "react-i18next";

// Flag SVGs (reused from LanguageSwitcher style)
const flags = {
  en: (
    <svg viewBox="0 0 32 24" className="w-7 h-5 rounded-sm shadow">
      <rect width="32" height="24" fill="#012169" />
      <path d="M0 0L32 24M32 0L0 24" stroke="white" strokeWidth="4" />
      <path d="M0 0L32 24M32 0L0 24" stroke="#C8102E" strokeWidth="2.5" />
      <path d="M16 0V24M0 12H32" stroke="white" strokeWidth="8" />
      <path d="M16 0V24M0 12H32" stroke="#C8102E" strokeWidth="5" />
    </svg>
  ),
  nl: (
    <svg viewBox="0 0 32 24" className="w-7 h-5 rounded-sm shadow">
      <rect width="32" height="8" fill="#AE1C28" />
      <rect y="8" width="32" height="8" fill="white" />
      <rect y="16" width="32" height="8" fill="#21468B" />
    </svg>
  ),
  fr: (
    <svg viewBox="0 0 32 24" className="w-7 h-5 rounded-sm shadow">
      <rect width="10.67" height="24" fill="#002395" />
      <rect x="10.67" width="10.67" height="24" fill="white" />
      <rect x="21.33" width="10.67" height="24" fill="#ED2939" />
    </svg>
  ),
  es: (
    <svg viewBox="0 0 32 24" className="w-7 h-5 rounded-sm shadow">
      <rect width="32" height="24" fill="#FFC400" />
      <rect y="6" width="32" height="12" fill="#AA151B" />
    </svg>
  ),
};

const languageNames = {
  en: "English",
  nl: "Nederlands",
  fr: "Français",
  es: "Español",
};

// Proper Instagram brand glyph (camera outline + lens + flash dot)
const InstagramGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-7 h-7 text-white"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// WhatsApp brand glyph (phone receiver inside chat bubble)
const WhatsAppGlyph = () => (
  <svg
    viewBox="0 0 32 32"
    fill="white"
    className="w-7 h-7"
    aria-hidden="true"
  >
    <path d="M16.001 3.2C8.93 3.2 3.2 8.93 3.2 16.001c0 2.255.594 4.45 1.722 6.382L3.2 28.8l6.59-1.72a12.78 12.78 0 006.211 1.581h.005c7.07 0 12.794-5.73 12.794-12.8 0-3.417-1.331-6.63-3.748-9.046A12.738 12.738 0 0016.001 3.2zm0 23.366h-.004a10.62 10.62 0 01-5.41-1.482l-.387-.23-4.013 1.05 1.072-3.91-.252-.4a10.6 10.6 0 01-1.625-5.633c0-5.872 4.78-10.65 10.652-10.65 2.844 0 5.515 1.109 7.524 3.121a10.586 10.586 0 013.117 7.532c-.003 5.872-4.781 10.602-10.674 10.602zm5.854-7.97c-.32-.16-1.897-.937-2.191-1.044-.293-.107-.507-.16-.72.16-.215.32-.829 1.044-1.016 1.258-.187.214-.374.241-.694.08-.32-.16-1.352-.498-2.575-1.589-.952-.849-1.594-1.897-1.781-2.217-.187-.32-.02-.493.14-.652.144-.143.32-.374.48-.561.16-.187.214-.32.32-.534.107-.214.054-.401-.027-.561-.08-.16-.72-1.736-.987-2.376-.26-.624-.524-.54-.72-.55l-.614-.011a1.18 1.18 0 00-.854.401c-.293.32-1.122 1.097-1.122 2.674s1.149 3.1 1.309 3.314c.16.214 2.26 3.452 5.476 4.84.766.331 1.362.528 1.827.677.768.244 1.467.21 2.02.127.616-.092 1.897-.775 2.166-1.524.267-.749.267-1.391.187-1.524-.08-.134-.293-.214-.614-.374z" />
  </svg>
);

const WhatsAppButton = () => {
  const { i18n } = useTranslation();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const phoneNumber = "31615460576";
  const message =
    "Hello! I'm interested in your services and would like to discuss my project.";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
    message
  )}`;
  const instagramUrl = "https://www.instagram.com/basic.job/";

  const currentLang = ["en", "nl", "fr", "es"].includes(i18n.language)
    ? i18n.language
    : "en";

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-4 items-end"
      data-testid="floating-buttons"
    >
      {/* Language Selector */}
      <div className="relative">
        {isLanguageOpen && (
          <div className="absolute bottom-full right-0 mb-3 bg-white rounded-xl shadow-2xl p-2 min-w-[180px] border border-gray-100">
            {Object.keys(flags).map((code) => (
              <button
                key={code}
                onClick={() => changeLanguage(code)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  i18n.language === code
                    ? "bg-[#1a7a8a]/10 text-[#1a7a8a] font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                data-testid={`lang-option-${code}`}
              >
                {flags[code]}
                <span className="text-sm font-medium">
                  {languageNames[code]}
                </span>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={() => setIsLanguageOpen(!isLanguageOpen)}
          className="bg-white hover:bg-gray-50 rounded-full p-3 shadow-2xl transform transition-all duration-300 hover:scale-110 flex items-center justify-center ring-2 ring-[#1a7a8a]"
          aria-label="Change Language"
          data-testid="floating-language-btn"
        >
          {flags[currentLang]}
        </button>
      </div>

      {/* Instagram Button */}
      <a
        href={instagramUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group block"
        aria-label="Follow us on Instagram"
        data-testid="floating-instagram-btn"
      >
        <button
          className="rounded-full p-4 shadow-2xl transform transition-all duration-300 hover:scale-110 flex items-center justify-center"
          style={{
            background:
              "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
          }}
        >
          <InstagramGlyph />
        </button>
      </a>

      {/* WhatsApp Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative block"
        aria-label="Chat with us on WhatsApp"
        data-testid="floating-whatsapp-btn"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75"></div>
          <button className="relative bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transform transition-all duration-300 hover:scale-110 flex items-center justify-center">
            <WhatsAppGlyph />
          </button>
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </a>
    </div>
  );
};

export default WhatsAppButton;
